"use client";
import { useState, useRef, useEffect } from "react";
import { Send, CheckCircle2, Loader2, ChevronDown, X } from "lucide-react";
import { stateCityData } from "@/utils/stateCityData";
import { sendOtp, verifyOtp } from "@/helpers/OTPModule";
import { getUTMParams } from "@/helpers/getUTMParams";
import { downloadBrochure } from "@/utils/downloadBrochure"
const SKILLING_CENTERS = [
    { value: "delhi", label: "Delhi" },
    { value: "kolkata", label: "Kolkata" },
];

const STATES = Object.keys(stateCityData).map((state) => ({
    value: state,
    label: state,
}));

function CustomDropdown({ placeholder, options, value, onChange, disabled = false }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = options.find((o) => o.value === value);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((o) => !o)}
                className={`w-full flex items-center justify-between px-4 py-2 rounded-md border text-sm transition-all duration-200 ${disabled
                    ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
                    : open
                        ? "bg-white border-blue-400 shadow-[0_0_0_3px_rgba(59,130,246,0.12)]"
                        : "bg-gray-50 border-gray-200 hover:border-gray-300 text-gray-700"
                    }`}
            >
                <span className={selected ? "text-gray-900 font-medium" : "text-gray-400"}>
                    {selected ? selected.label : placeholder}
                </span>
                <ChevronDown
                    className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""
                        }`}
                />
            </button>

            {open && (
                <div className="absolute left-0 top-full z-990 w-full mt-1.5 bg-white rounded-2xl border border-gray-100 shadow-xl max-h-50 overflow-y-auto">
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                                onChange(opt.value);
                                setOpen(false);
                            }}
                            className={`w-full text-left px-4 py-[0.5px] text-sm transition-colors ${value === opt.value
                                ? "bg-blue-500 text-white font-semibold"
                                : "text-gray-700 hover:bg-gray-50"
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdmissionForm() {
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        mobile: "",
        skillingCenter: "",
        state: "",
        city: "",
    });
    const [utm, setUtm] = useState({
        utm_source: "direct",
        utm_medium: "website",
        utm_campaign: "unknown",
        utm_adgroup: "",
        utm_term: "",
    });

    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otp, setOtp] = useState("");

    const [countdown, setCountdown] = useState(0);
    const [loadingOtp, setLoadingOtp] = useState(false);
    const [loadingVerify, setLoadingVerify] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [formError, setFormError] = useState("");

    useEffect(() => {
        if (countdown <= 0) return;
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [countdown]);

    useEffect(() => {
        const params = getUTMParams();
        if (Object.keys(params).length > 0) {
            setUtm((prev) => ({ ...prev, ...params }));
        }
    }, []);

    const set = (key, val) => {
        setForm((f) => ({ ...f, [key]: val }));
        setFormError("");
    };
    const cityOptions =
        stateCityData[form.state]?.map((city) => ({
            value: city,
            label: city,
        })) || [];
    // ── Send OTP ──
    const handleSendOTP = async () => {
        if (form.mobile.length !== 10) {
            setFormError("Enter a valid 10-digit mobile number.");
            return;
        }
        setFormError("");
        // setOtpError("");
        setLoadingOtp(true);
        try {
            const result = await sendOtp(form.mobile);

            if (result.success) {
                setOtpSent(true);
                setCountdown(30);
                // setFormError("");
            } else {
                setFormError(result.message);
            }

        } catch {
            setFormError("Failed to send OTP. Please try again.");
        } finally {
            setLoadingOtp(false);
        }
    };

    // ── Verify OTP ──
    const otpRegex = /^\d{4}$/;

    const handleVerifyOTP = async () => {
        if (!otpRegex.test(otp)) {
            setFormError("OTP must be exactly 4 digits");
            return;
        }

        setFormError("");
        setLoadingVerify(true);

        try {
            const result = await verifyOtp(form.mobile, otp);

            if (result.success) {
                setOtpVerified(true);
            }
            else {
                setFormError(result.message);
            }

        } catch (error) {
            setFormError("Invalid OTP. Please try again.");
            setOtp("");
        } finally {
            setLoadingVerify(false);
        }
    };

    // ── Submit ──
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.fullName.trim())
            return setFormError("Full name is required.");

        if (!form.email.trim())
            return setFormError("Email is required.");

        if (!form.mobile.trim())
            return setFormError("Mobile number is required.");

        if (!otpVerified)
            return setFormError("Please verify your mobile number first.");

        if (!form.skillingCenter)
            return setFormError("Please choose a training centre.");

        if (!form.state)
            return setFormError("Please select your state.");

        if (!form.city)
            return setFormError("Please select your city.");
        setFormError("");
        setLoadingSubmit(true);


        const marritoPayload = {
            name: form.fullName.trim(),
            email: form.email.trim(),
            contact_number: form.mobile,
            state: form.state,
            city: form.city,
            specialization: "Hospitality and Hotel Management (Work int-3 Years)",
        };


        const sheetPayload = {
            name: form.fullName.trim(),
            email: form.email.trim(),
            contact_number: form.mobile,
            state: form.state,
            city: form.city,
            schoolName: "Medhavi Skills University",
            skillingCenter: form.skillingCenter,
            downloadStatus: `${utm.utm_source}_${utm.utm_medium}_${utm.utm_campaign}`,

            source: utm.utm_source,
            medium: utm.utm_medium,
            campaign: utm.utm_campaign,
            program: "B.Sc. in Hospitality & Hotel Management",

        };
        try {

            const resultMerito = await fetch('https://partner.medhaviskillsuniversity.edu.in/partnar/student-lead-for-wise-landing-page/',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(marritoPayload),
                }
            );

            await resultMerito.json();

            const response = await fetch(
                "https://lp.msu.edu.in/lead/api/wise-hospitality-hotel-management",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(sheetPayload),
                }
            );

            const result = await response.json();
            if (!result.success) {
                throw new Error(result.message || "Failed to submit form");
            }

            setForm({
                fullName: "",
                email: "",
                mobile: "",
                skillingCenter: "",
                state: "",
                city: "",
            });

            setOtp("");
            setOtpSent(false);
            setOtpVerified(false);
            setCountdown(0);
            setFormError("");
            await downloadBrochure('https://cdn.msu.edu.in/pdf/HHM_Brochure_2026.pdf', 'HHM_Brochure_2026.pdf');
        } catch {
            setFormError("Submission failed. Please try again.");
        } finally {
            setLoadingSubmit(false);
        }
    };


    return (
        <div className="bg-white rounded-md shadow-2xl overflow-visible max-w-2xl w-full mx-auto">

            <div className="flex justify-center pt-7 pb-1">
                <span className="bg-[#0475B3] text-white text-[11px] font-bold tracking-widest uppercase px-4 py-1.5 rounded-full">
                    Limited Seats Available
                </span>
            </div>

            <div className="px-8 pb-8 pt-4">
                <h2 className="text-[1.75rem] font-extrabold text-gray-900 text-center mb-7 tracking-tight">
                    Admission 2026
                </h2>

                <form onSubmit={handleSubmit} className="space-y-3.5" noValidate>

                    {/* Hidden UTM Fields */}
                    <input type="hidden" name="utm_source" />
                    <input type="hidden" name="utm_medium" />
                    <input type="hidden" name="utm_campaign" />
                    <input type="hidden" name="utm_adgroup" />
                    <input type="hidden" name="utm_term" />
                    {/* Full Name */}
                    <input
                        type="text"
                        placeholder="Enter your full name"
                        value={form.fullName}
                        onChange={(e) => set("fullName", e.target.value)}
                        className="w-full px-4 py-2 rounded-md bg-gray-50 border border-gray-200 text-sm
                         text-gray-900 placeholder-gray-400 outline-none transition-all focus:border-blue-400
                          focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.12)]"
                    />

                    <input
                        type="email"
                        placeholder="your.email@example.com"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        className="w-full px-4 py-2 rounded-md bg-gray-50 border border-gray-200 text-sm
                         text-gray-900 placeholder-gray-400 outline-none transition-all focus:border-blue-400
                          focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.12)]"
                    />

                    <div className="flex gap-2 items-center">
                        <input
                            type="tel"
                            placeholder="10-digit mobile number"
                            maxLength={10}
                            value={form.mobile}
                            onChange={(e) => {
                                set("mobile", e.target.value.replace(/\D/g, ""));
                                if (otpSent) {
                                    setOtpSent(false);
                                    setOtpVerified(false);
                                    setOtp("");
                                    setFormError("");
                                }
                            }}
                            disabled={otpVerified}
                            className={`flex-1 px-4 py-2 rounded-md  border text-s outline-none transition-all
                                ${otpVerified
                                    ? "border-[#FC9D00]  text-black cursor-not-allowed"
                                    : "border-gray-200 text-gray-900 focus:border-blue-400 focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.12)]"
                                }`}
                        />
                        {otpVerified ? (
                            <div className="flex items-center gap-1.5 text-[#FC9D00] text-sm font-semibold px-3 whitespace-nowrap">
                                <CheckCircle2 className="w-4 h-4" /> Verified
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={otpSent && countdown > 0 ? undefined : handleSendOTP}
                                disabled={loadingOtp || (otpSent && countdown > 0)}
                                className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-2xl whitespace-nowrap transition-all
                                    ${loadingOtp || (otpSent && countdown > 0)
                                        ? "text-gray-400 cursor-not-allowed"
                                        : "text-[#0475B3] hover:text-[#0475B3]/90"
                                    }`}
                            >
                                {loadingOtp
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <Send className="w-2 h-2" />
                                }
                                {otpSent && countdown > 0 ? `${countdown}s` : otpSent ? "Resend" : "Send OTP"}
                            </button>
                        )}
                    </div>

                    {/* OTP Entry */}
                    {otpSent && !otpVerified && (
                        <div className="space-y-2">

                            <div className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={otp}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "");
                                        setOtp(value);
                                        setFormError("");
                                    }}
                                    placeholder="Enter OTP"
                                    disabled={loadingVerify}
                                    className="flex-1 px-4 py-2 rounded-md border border-gray-200 text-sm text-gray-900
                                     outline-none transition-all focus:border-[#0475B3] focus:bg-white"
                                />

                                <button
                                    type="button"
                                    onClick={handleVerifyOTP}
                                    disabled={loadingVerify || otp.length !== 4}
                                    className="px-4 py-2 rounded-md bg-[#0475B3] hover:bg-[#0475B3]/90 text-white text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                    {loadingVerify ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Verify OTP"
                                    )}
                                </button>
                            </div>

                        </div>
                    )}
                    {/* 
                    {formError && (
                        <p className="text-xs text-red-500 font-medium">
                            {formError}
                        </p>
                    )} */}

                    {/* Skilling Center */}
                    <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1.5">
                            Choose Your Training Centre <span className="text-red-500">*</span>
                        </p>
                        <CustomDropdown
                            placeholder="Select Training Centre"
                            options={SKILLING_CENTERS}
                            value={form.skillingCenter}
                            onChange={(v) => set("skillingCenter", v)}
                        />
                    </div>

                    {/* State */}
                    <CustomDropdown
                        placeholder="Select State"
                        options={STATES}
                        value={form.state}
                        onChange={(v) => {
                            set("state", v);
                            set("city", "");
                        }}
                    />

                    {/* City */}
                    <CustomDropdown
                        placeholder="Select City"
                        options={cityOptions}
                        value={form.city}
                        onChange={(v) => set("city", v)}
                        disabled={!form.state}
                    />


                    {formError && (
                        <p className="text-xs text-red-500 font-medium">
                            {formError}
                        </p>
                    )}
                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loadingSubmit}
                        className="w-full py-2 rounded-md bg-[#0475B3] hover:bg-[#0475B3]/60 active:scale-[0.99] text-white font-bold text-sm tracking-wide transition-all duration-200 shadow-lg shadow-blue-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-1"
                    >
                        {loadingSubmit && <Loader2 className="w-4 h-4 animate-spin" />}
                        Download Brochure Now
                    </button>

                    <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                        By submitting this form, you agree to receive communication from Medhavi Skills University
                    </p>
                </form>
            </div>
        </div>
    );
}